import { BookingStatus } from '@/hotdesk-ui/enum/booking-status';
import { BookingStatusTabs } from '@/hotdesk-ui/enum/booking-status';
import {
    BOOKING_FILTER_PARAM_KEYS,
    BOOKING_DATE_PARAM_KEYS,
    BOOKING_PRICE_PARAM_KEYS,
    BOOKING_SEARCH_PARAM_KEY,
    BOOKING_SORT_PARAM_KEY,
    BOOKING_CURRENCY_PARAM_KEY,
    BOOKING_STATUS_FILTER_PARAM_KEY,
    BOOKING_SORT_ORDER_BY,
    BookingSortOption,
    BookingStatusFilterOption
} from '@/hotdesk-ui/constants/booking-filters.constant';
import {
    toApiStartDateTimeGte,
    toApiStartDateTimeLte,
    toFilterDateValue
} from '@/hotdesk-ui/helpers/booking-filter-date-range';
import { getCurrentDate } from '@/hotdesk-ui/utils/status';

const UPCOMING_STATUS_IN = [
    BookingStatus.CONFIRMED_QUICK,
    BookingStatus.CONFIRMED_BY_HOST,
    BookingStatus.CONFIRMED_BY_SYSTEM,
    BookingStatus.PENDING_CAPTURE_MONEY
];

// A host can mark a no-show while the booking is still running. Ongoing keeps
// it until its end passes (the guest can still check in, which the backend
// undoes the mark on); History picks it up from then on.
const ONGOING_STATUS_IN = [...UPCOMING_STATUS_IN, BookingStatus.NO_SHOW];

const STATUS_FILTER_MAP: Record<BookingStatusFilterOption, BookingStatus[]> = {
    completed: [BookingStatus.COMPLETED],
    expired: [BookingStatus.EXPIRED],
    declined: [
        BookingStatus.CANCELLED_BY_HOST
    ],
    cancelled: [BookingStatus.CANCELLED_BY_USER]
};

const resolveStatusFilterIn = (
    filters: Record<string, string>
): BookingStatus[] | undefined => {
    const raw = filters[BOOKING_STATUS_FILTER_PARAM_KEY];
    if (!raw) return undefined;
    const statusIn = raw
        .split(',')
        .flatMap(
            (value) =>
                STATUS_FILTER_MAP[value as BookingStatusFilterOption] ?? []
        );
    return statusIn.length ? statusIn : undefined;
};

type SearchParamsLike =
    | URLSearchParams
    | Record<string, string | string[] | undefined>;

const readParam = (
    searchParams: SearchParamsLike,
    key: string
): string | undefined => {
    if (searchParams instanceof URLSearchParams) {
        const value = searchParams.get(key);
        return value || undefined;
    }
    const raw = searchParams[key];
    if (raw == null) return undefined;
    return Array.isArray(raw) ? raw[0] : raw;
};

export const bookingFiltersFromSearchParams = (
    searchParams: SearchParamsLike
): Record<string, string> => {
    const filters: Record<string, string> = {};
    for (const key of BOOKING_FILTER_PARAM_KEYS) {
        const value = readParam(searchParams, key);
        if (value) filters[key] = value;
    }
    for (const key of BOOKING_PRICE_PARAM_KEYS) {
        const value = readParam(searchParams, key);
        if (value) filters[key] = value;
    }
    for (const key of BOOKING_DATE_PARAM_KEYS) {
        const value = readParam(searchParams, key);
        if (value) filters[key] = toFilterDateValue(value);
    }
    const search = readParam(searchParams, BOOKING_SEARCH_PARAM_KEY);
    if (search) filters[BOOKING_SEARCH_PARAM_KEY] = search;
    const sortBy = readParam(searchParams, BOOKING_SORT_PARAM_KEY);
    if (sortBy) filters[BOOKING_SORT_PARAM_KEY] = sortBy;
    const statusFilter = readParam(searchParams, BOOKING_STATUS_FILTER_PARAM_KEY);
    if (statusFilter) filters[BOOKING_STATUS_FILTER_PARAM_KEY] = statusFilter;
    const currency = readParam(searchParams, BOOKING_CURRENCY_PARAM_KEY);
    if (currency) filters[BOOKING_CURRENCY_PARAM_KEY] = currency;
    return filters;
};

const splitCsv = (value: string): string[] =>
    value.split(',').filter(Boolean);

const applySharedFilters = (
    params: Record<string, unknown>,
    filters: Record<string, string>,
    options?: { includeDateRange?: boolean }
) => {
    if (filters.listingTypeIn) {
        params.listingTypeIn = splitCsv(filters.listingTypeIn);
    }
    if (filters.plans) {
        params.plans = splitCsv(filters.plans);
    }
    if (filters.search?.trim()) {
        params.search = filters.search.trim();
    }
    if (filters.priceGte != null && filters.priceGte !== '') {
        params.priceGte = Number(filters.priceGte);
    }
    if (filters.priceLte != null && filters.priceLte !== '') {
        params.priceLte = Number(filters.priceLte);
    }
    if (
        filters[BOOKING_CURRENCY_PARAM_KEY] &&
        ((filters.priceGte != null && filters.priceGte !== '') ||
            (filters.priceLte != null && filters.priceLte !== ''))
    ) {
        params.currency = filters[BOOKING_CURRENCY_PARAM_KEY];
    }

    if (options?.includeDateRange === false) return;

    const startGte = toApiStartDateTimeGte(filters.startDateTimeGte ?? '');
    const startLte = toApiStartDateTimeLte(filters.startDateTimeLte ?? '');
    if (startGte) params.startDateTimeGte = startGte;
    if (startLte) params.startDateTimeLte = startLte;
};

const TAB_DEFAULT_ORDER_BY: Record<BookingStatusTabs, string> = {
    [BookingStatusTabs.pending]: 'details__expiration_threshold',
    [BookingStatusTabs.upcoming]: 'details__start_date_time',
    [BookingStatusTabs.ongoing]: 'details__end_date_time',
    // Sorted on the checkout date the card actually renders. It used to be
    // `-updated_at`, i.e. when the row was last touched (status flip, payment
    // capture, expiry cron), which bore no relation to any date on screen and
    // read as unsorted.
    [BookingStatusTabs.history]: '-details__end_date_time'
};

/**
 * Appends `id` as a tiebreaker so the ordering is a total order.
 *
 * Every sort key above is non-unique (several bookings routinely share a
 * start/end time), and rows that tie have no defined order between two
 * queries. A tie group straddling a LIMIT/OFFSET boundary can therefore
 * repeat a row on the next page and drop another. Matching the tiebreaker's
 * direction to the primary key keeps the visible sequence intuitive.
 *
 * Requires `id` to be whitelisted on the backend's booking OrderingFilter --
 * django-filter rejects the whole request with a 400 otherwise.
 */
const withIdTiebreaker = (orderBy: string): string =>
    orderBy.startsWith('-') ? `${orderBy},-id` : `${orderBy},id`;

const resolveOrderBy = (
    tab: BookingStatusTabs,
    filters: Record<string, string>
): string => {
    const sortBy = filters[BOOKING_SORT_PARAM_KEY] as
        | BookingSortOption
        | undefined;
    if (sortBy && sortBy in BOOKING_SORT_ORDER_BY) {
        return withIdTiebreaker(BOOKING_SORT_ORDER_BY[sortBy]);
    }
    return withIdTiebreaker(TAB_DEFAULT_ORDER_BY[tab]);
};

export const buildBookingListParams = (
    tab: BookingStatusTabs,
    filters: Record<string, string>,
    pagination: { limit: number; offset: number }
) => {
    const params: Record<string, unknown> = {
        limit: pagination.limit,
        offset: pagination.offset,
        // My Bookings means bookings this user *placed*. The endpoint's
        // queryset is wider than that by design -- it ORs in
        // `listing__owner` and `listing__workspace__owner` so hosts can read
        // bookings on their own spaces -- so without this a host saw other
        // people's bookings on their listings mixed into their own list.
        //
        // Caveat: the backend's `my_bookings` filters on `user=request.user`
        // alone, so a booking placed through guest checkout and later linked
        // to this account (`guest_user__user`) drops out of the list. No
        // single param covers both; widening it needs a backend change.
        myBookings: true
    };

    const now = getCurrentDate();

    switch (tab) {
        case BookingStatusTabs.pending:
            params.statusIn = [BookingStatus.PENDING_CONFIRMATION_BY_HOST];
            params.orderBy = resolveOrderBy(tab, filters);
            applySharedFilters(params, filters);
            break;

        case BookingStatusTabs.upcoming:
            params.statusIn = UPCOMING_STATUS_IN;
            params.orderBy = resolveOrderBy(tab, filters);
            params.upcomingBookings = now;
            applySharedFilters(params, filters, { includeDateRange: false });
            {
                // `upcoming__filter` alone defines this tab -- do NOT re-assert
                // `start >= now` here. With `api_check_in_filter` ON (its state
                // in production) that filter deliberately also matches a
                // check-in-required booking whose start has PASSED but which
                // the user has not checked into yet: still `end > now`, still
                // theirs to walk into. Sending `start_date_time__gte=now`
                // excluded exactly those rows again, and Ongoing's branch wants
                // `user_checked_in=True`, so a late booking appeared in no tab
                // at all -- while its check-in was the thing the user had come
                // to find. Redundant with the flag OFF too, since that branch
                // requires `start > now` on its own.
                const userStartGte = toApiStartDateTimeGte(
                    filters.startDateTimeGte ?? ''
                );
                if (userStartGte) params.startDateTimeGte = userStartGte;
                const startLte = toApiStartDateTimeLte(
                    filters.startDateTimeLte ?? ''
                );
                if (startLte) params.startDateTimeLte = startLte;
            }
            break;

        case BookingStatusTabs.ongoing:
            params.statusIn = ONGOING_STATUS_IN;
            params.orderBy = resolveOrderBy(tab, filters);
            params.onGoingBookings = now;
            applySharedFilters(params, filters, { includeDateRange: false });
            params.startDateTimeLte = now;
            params.endDateTimeGte = now;
            {
                const userStartGte = toApiStartDateTimeGte(
                    filters.startDateTimeGte ?? ''
                );
                if (userStartGte) params.startDateTimeGte = userStartGte;
                const userStartLte = toApiStartDateTimeLte(
                    filters.startDateTimeLte ?? ''
                );
                if (userStartLte && userStartLte < now) {
                    params.startDateTimeLte = userStartLte;
                }
            }
            break;

        case BookingStatusTabs.history:
        default: {
            // `history__filter` alone defines this tab: a cancelled, completed,
            // expired or failed booking whatever its dates, plus a confirmed
            // or no-show one whose end has passed. It replaced a status list
            // ANDed with `end_date_time__lte=now`, which dropped every booking
            // cancelled before its end -- no other tab takes those statuses,
            // so it rendered nowhere. Do NOT re-add an end-date guard here.
            //
            // Requires the backend to know the param: django-filter ignores
            // unknown ones, which would leave this tab unbounded.
            params.historyBookings = now;
            // Narrows the tab further when a status chip is picked.
            const statusFilterIn = resolveStatusFilterIn(filters);
            if (statusFilterIn) params.statusIn = statusFilterIn;
            params.orderBy = resolveOrderBy(tab, filters);
            applySharedFilters(params, filters);
            break;
        }
    }

    if (filters[BOOKING_SORT_PARAM_KEY] === 'recently_completed') {
        params.statusIn = [BookingStatus.COMPLETED];
    }

    return params;
};

const MULTI_SELECT_FILTER_KEYS = new Set(['listingTypeIn', 'plans']);

export const countActiveBookingFilters = (
    filters: Record<string, string>
): number => {
    let count = BOOKING_FILTER_PARAM_KEYS.reduce((acc, key) => {
        const value = filters[key];
        if (!value) return acc;
        if (MULTI_SELECT_FILTER_KEYS.has(key)) {
            return acc + (splitCsv(value).length > 0 ? 1 : 0);
        }
        return acc + 1;
    }, 0);

    const priceGte = filters.priceGte;
    const priceLte = filters.priceLte;
    if (
        (priceGte && priceGte !== '0') ||
        (priceLte && priceLte !== '' && priceLte !== '0')
    ) {
        count += 1;
    }

    if (filters.startDateTimeGte || filters.startDateTimeLte) {
        count += 1;
    }

    if (filters[BOOKING_SORT_PARAM_KEY]) {
        count += 1;
    }

    if (filters[BOOKING_STATUS_FILTER_PARAM_KEY]) {
        count += 1;
    }

    return count;
};

export const hasActiveBookingFilters = (
    filters: Record<string, string>
): boolean =>
    countActiveBookingFilters(filters) > 0 ||
    Boolean(filters[BOOKING_SEARCH_PARAM_KEY]?.trim());
